import React, { useState, useEffect } from 'react';
import { 
  Modal, Form, Input, InputNumber, Select, Upload, Switch,
  Button, Tabs, Spin, notification, Tag, Space, Table, Tooltip
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import packService from '../../../../services/packService';
import categoryService from '../../../../services/categoryService';
import userService from '../../../../services/userService';
import config from '../../../../config';
import ClientOptionLabel, { buildClientSearchText, getClientSelectedLabel } from '../../../common/ClientOptionLabel';

const { Option } = Select;
const { TabPane } = Tabs;
const { TextArea } = Input;

/**
 * Edit Product Modal Component
 */
const EditModal = ({ visible, packId, onCancel, onSuccess,userso }) => {
  const [form] = Form.useForm();
  const [product, setProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isRentalProduct, setIsRentalProduct] = useState(false);
  const [isPack, setIsPack] = useState(false);
  const [customPrices, setCustomPrices] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customPrice, setCustomPrice] = useState(0);
  
  // Fetch product data, categories, and users when modal opens
  useEffect(() => {
    if(visible && packId) {
      setProduct(null);
      setUsers(userso);

      fetchProductData();
      fetchCategories();
    }
    
  }, [visible, packId, userso]);
  
  const fetchProductData = async () => {
    try {
      setLoading(true);
      const response = await packService.getPackById(packId);
      const productData = response.data;
      
      setProduct(productData);
      
      // Determine if it's a rental product
      const isRental = productData.priceone > 0 || productData.pricetwo > 0 || 
                      productData.pricethree > 0 || productData.pricefour > 0;
      setIsRentalProduct(isRental);
      
      // Set isPack based on is_pack field
      setIsPack(productData.is_pack === 1 || productData.is_pack === '1');
      
      // Set form values
      form.setFieldsValue({
        product_title: productData.name,
        product_code: productData.id,
        product_description: productData.description,
        category_id: 0,
        product_price: parseFloat(productData.price) || 0,
        product_slug: productData.slug,
        cost_price: parseFloat(productData.cost_price) || 0,
        quantity: parseInt(productData.quantity) || 0,
        status: productData.status === '1' || productData.status === 1,
        priceone: parseFloat(productData.priceone) || 0,
        pricetwo: parseFloat(productData.pricetwo) || 0,
        isPack:true,
        pricethree: parseFloat(productData.pricethree) || 0,
        pricefour: parseFloat(productData.pricefour) || 0,
      });
      
      // Load custom prices if available
      if (productData.custom_prices && productData.custom_prices.length > 0 && userso.length > 0) {
        setCustomPrices(productData.custom_prices.map(cp => ({
          key: cp.id,
          userId: cp.user_id,
          price: parseFloat(cp.price),
          username: cp.user?.name || userso.find(u => u.id === cp.user_id)?.name || `User #${cp.user_id}`
        })));
      }
      
      // Set image file if exists
      if (productData.image) {
        setFileList([
          {
            uid: '-1',
            name: productData.image.split('/').pop(),
            status: 'done',
            url:  `${productData.image}`,
          },
        ]);
      } else {
        setFileList([]);
      }
    } catch (error) {
      notification.error({
        message: 'Error loading product',
        description: error.message || 'Failed to load product data'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const fetchCategories = async () => {
    try {
      const response = await categoryService.getAllCategories();
      setCategories(response.data.data || []);
    } catch (error) {
      notification.error({
        message: 'Error fetching categories',
        description: error.message || 'Failed to load categories'
      });
    }
  };
  
  const fetchUsers = async () => {
    try {
      const params = { limit: 300 };
      const response = await userService.getAllCustomers(params);
      setUsers(response.data || []);
    } catch (error) {
      notification.error({
        message: 'Error fetching users',
        description: error.message || 'Failed to load users'
      });
    }
  };
  
  // Image upload props
  const uploadProps = {
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    beforeUpload: (file) => {
      // Validate file type
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        notification.error({
          message: 'Invalid file type',
          description: 'You can only upload image files!'
        });
        return Upload.LIST_IGNORE;
      }
      
      // Validate file size (2MB limit)
      const isLessThan2MB = file.size / 1024 / 1024 < 2;
      if (!isLessThan2MB) {
        notification.error({
          message: 'File too large',
          description: 'Image must be smaller than 2MB!'
        });
        return Upload.LIST_IGNORE;
      }
      
      // Replace existing file
      setFileList([file]);
      return false;
    },
    fileList,
  };
  
  // Handle custom price addition
  const handleAddCustomPrice = () => {
    
    if (!selectedCustomer || customPrice <= 0) {
      notification.error({
        message: 'Invalid custom price',
        description: 'Please select a customer and enter a valid price'
      });
      return;
    }
    console.log('Adding custom price:', selectedCustomer, customPrice);
    // Check if this customer already has a custom price
    const existingIndex = customPrices.findIndex(cp => cp.userId === selectedCustomer);
    if (existingIndex >= 0) {
      // Update existing custom price
      const newPrices = [...customPrices];
      newPrices[existingIndex].price = customPrice;
      setCustomPrices(newPrices);
      console.log('Updated custom price:', newPrices);
    } else {
      // Add new custom price
      const selectedUser = userso.find(u => u.id === selectedCustomer);
      setCustomPrices([
        ...customPrices,
        {
          key: `${selectedCustomer}_${Date.now()}`,
          userId: selectedCustomer,
          price: customPrice,
          username:   userso.find(u => u.id === selectedCustomer)?.name
        }
      ]);
      console.log('Added custom price:', customPrices);
    }
    
    // Reset selection
    setSelectedCustomer(null);
    setCustomPrice(0);
  };
  
  // Handle custom price deletion
  const handleDeleteCustomPrice = (key) => {
    setCustomPrices(customPrices.filter(cp => cp.key !== key));
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      
      // Prepare form data for API request
      const formData = new FormData();
      
      // Add basic fields
      Object.keys(values).forEach(key => {
        // Skip rental pricing fields if not a rental product
        if (!isRentalProduct && ['priceone', 'pricetwo', 'pricethree', 'pricefour'].includes(key)) {
          return;
        }
        
        // Convert boolean status to number
        if (key === 'status') {
          formData.append(key, values[key] ? '1' : '0');
          return;
        }
        
        // Add other fields
        if (values[key] !== undefined && values[key] !== null) {
          formData.append(key, values[key]);
        }
      });
      
      // Add is_pack flag
      formData.append('is_pack', isPack ? 1 : 0);
      
      if (customPrices.length > 0) {
        customPrices.forEach((cp, index) => {
          formData.append(`customer_ids[${index}]`, cp.userId);
          formData.append(`customer_prices[${index}]`, cp.price);
        });
      }
      // Add new image if exists
      const hasNewImage = fileList.length > 0 && !fileList[0].url;
      if (hasNewImage) {
        formData.append('image', fileList[0]);
      } else if (fileList.length > 0) {
        // Keep existing image
        formData.append('old_image', product.image);
      }
      
      // Call API to update product
      await packService.updatePack(packId, formData);
      
      notification.success({
        message: 'Product updated successfully',
        description: 'The product information has been updated.'
      });
      
      onSuccess();
      
    } catch (error) {
      notification.error({
        message: 'Failed to update product',
        description: error.message || 'Please check your inputs and try again.'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle category change to determine if rental product
  const handleCategoryChange = (value) => {
    // Find if the selected category is a rental type
    const category = categories.find(c => c.id === value);
    if (category) {
      // Assuming category type ID 2 is for rental products
      const isRental = category.category_type_id === 2;
      setIsRentalProduct(isRental);
      
      // Reset rental prices if switching from rental to non-rental
      if (!isRental) {
        form.setFieldsValue({
          priceone: 0,
          pricetwo: 0,
          pricethree: 0,
          pricefour: 0
        });
      }
    }
  };
  
  // Custom price columns for table
  const customPriceColumns = [
    {
      title: 'Customer',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (price) => `£${parseFloat(price).toFixed(2)}`
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Tooltip title="Delete">
          <Button 
            type="link" 
            danger 
            icon={<DeleteOutlined />} 
            onClick={() => handleDeleteCustomPrice(record.key)}
          />
        </Tooltip>
      ),
    },
  ];
  
  return (
    <Modal
      title="Edit Product"
      open={visible}
      onCancel={onCancel}
      width={800}
      footer={[
        <Button key="back" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
          className="bg-golden-navy"
        >
          Update Product
        </Button>
      ]}
    >
      <Spin spinning={loading}>
        {product ? (
          <Form
            form={form}
            layout="vertical"
          >
            <Tabs defaultActiveKey="basic">
              <TabPane tab="Basic Information" key="basic">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Form.Item
                    name="product_title"
                    label="Product Title"
                    rules={[{ required: true, message: 'Please enter product title' }]}
                  >
                    <Input placeholder="Enter product title" />
                  </Form.Item>
                  
                  <Form.Item
                    name="product_code"
                    label="Product Code"
                    rules={[{ required: true, message: 'Please enter product code' }]}
                  >
                    <Input placeholder="Enter product code" />
                  </Form.Item>
                  
                  <Form.Item
                    name="category_id"
                    label="Category"
                    rules={[{ required: true, message: 'Please select a category' }]}
                  >
                    <Select
                      placeholder="Select a category"
                      onChange={handleCategoryChange}
                      showSearch
                      optionFilterProp="children"
                    >
                      {categories.map(category => (
                        <Option key={category.id} value={category.id}>
                          {category.category_title}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  
                  <Form.Item
                    name="quantity"
                    label="Quantity in Stock"
                    rules={[{ required: true, message: 'Please enter quantity' }]}
                  >
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                  
                  <Form.Item
                    name="product_description"
                    label="Product Description"
                    className="col-span-2"
                  >
                    <TextArea rows={4} placeholder="Enter product description" />
                  </Form.Item>
                  
                  <Form.Item
                    name="status"
                    label="Status"
                    valuePropName="checked"
                    className="col-span-2"
                  >
                    <Switch 
                      checkedChildren="Active" 
                      unCheckedChildren="Inactive" 
                    />
                  </Form.Item>
                  
                  <Form.Item
                    label="Is Pack"
                    className="col-span-2"
                  >
                    <Switch 
                      checked={isPack}
                      onChange={(checked) => setIsPack(checked)}
                      checkedChildren="Yes" 
                      unCheckedChildren="No" 
                    />
                  </Form.Item>
                </div>
              </TabPane>
              
              <TabPane tab="Pricing" key="pricing">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {!isRentalProduct ? (
                    <>
                      <Form.Item
                        name="product_price"
                        label="Selling Price"
                        rules={[{ required: true, message: 'Please enter selling price' }]}
                      >
                        <InputNumber 
                          min={0} 
                          step={0.01} 
                          style={{ width: '100%' }} 
                          prefix="£" 
                        />
                      </Form.Item>
                      
                      <Form.Item
                        name="cost_price"
                        label="Cost Price"
                        rules={[{ required: true, message: 'Please enter cost price' }]}
                      >
                        <InputNumber 
                          min={0} 
                          step={0.01} 
                          style={{ width: '100%' }} 
                          prefix="£" 
                        />
                      </Form.Item>
                    </>
                  ) : (
                    <>
                      <div className="col-span-2 mb-4">
                        <Tag color="blue">Rental Product</Tag>
                        <p className="text-sm text-gray-500 mt-2">
                          Please set the rental prices for different durations below:
                        </p>
                      </div>
                      
                      <Form.Item
                        name="priceone"
                        label="Price (3 Days)"
                        rules={[{ required: true, message: 'Please enter 3 days price' }]}
                      >
                        <InputNumber 
                          min={0} 
                          step={0.01} 
                          style={{ width: '100%' }} 
                          prefix="£" 
                        />
                      </Form.Item>
                      
                      <Form.Item
                        name="pricetwo"
                        label="Price (1 Week)"
                        rules={[{ required: true, message: 'Please enter 1 week price' }]}
                      >
                        <InputNumber 
                          min={0} 
                          step={0.01} 
                          style={{ width: '100%' }} 
                          prefix="£" 
                        />
                      </Form.Item>
                      
                      <Form.Item
                        name="pricethree"
                        label="Price (2 Weeks)"
                        rules={[{ required: true, message: 'Please enter 2 weeks price' }]}
                      >
                        <InputNumber 
                          min={0} 
                          step={0.01} 
                          style={{ width: '100%' }} 
                          prefix="£" 
                        />
                      </Form.Item>
                      
                      <Form.Item
                        name="pricefour"
                        label="Price (1 Month)"
                        rules={[{ required: true, message: 'Please enter 1 month price' }]}
                      >
                        <InputNumber 
                          min={0} 
                          step={0.01} 
                          style={{ width: '100%' }} 
                          prefix="£" 
                        />
                      </Form.Item>
                      
                      <Form.Item
                        name="cost_price"
                        label="Cost Price"
                        rules={[{ required: true, message: 'Please enter cost price' }]}
                        className="col-span-2"
                      >
                        <InputNumber 
                          min={0} 
                          step={0.01} 
                          style={{ width: '100%' }} 
                          prefix="£" 
                        />
                      </Form.Item>
                    </>
                  )}
                </div>
              </TabPane>

              <TabPane tab="Custom Pricing" key="customPricing">
                <div className="space-y-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-medium mb-2">Customer Specific Pricing</h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Set special prices for specific customers. These prices will override the default price for the selected customers.
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="col-span-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Select Customer
                        </label>
                        <Select
                          placeholder="Select customer"
                          value={selectedCustomer}
                          onChange={setSelectedCustomer}
                          style={{ width: '100%' }}
                          showSearch
                          optionFilterProp="search"
                          optionRender={(option) => <ClientOptionLabel client={option?.data?.client || option?.data} />}
                          options={(users || userso || []).map((user) => ({
                            value: user.id,
                            label: getClientSelectedLabel(user),
                            client: user,
                            search: buildClientSearchText(user),
                          }))}
                        />
                      </div>
                      
                      <div className="col-span-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Custom Price
                        </label>
                        <InputNumber
                          min={0}
                          step={0.01}
                          value={customPrice}
                          onChange={setCustomPrice}
                          style={{ width: '100%' }}
                          prefix="£"
                        />
                      </div>
                      
                      <div className="col-span-1 flex items-end">
                        <Button 
                          type="primary" 
                          onClick={handleAddCustomPrice}
                          className="bg-golden-navy"
                        >
                          Add Custom Price
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                 { customPrices && <Table
                    columns={customPriceColumns}
                    dataSource={customPrices}
                    rowKey="key"
                    pagination={false}
                    size="small"
                    locale={{ emptyText: 'No custom prices set' }}
                  />}
                </div>
              </TabPane>
              
              <TabPane tab="Image" key="image">
                <div className="grid grid-cols-1 gap-4">
                  <Form.Item
                    label="Product Image"
                    name="image"
                    valuePropName="fileList"
                    className="text-center"
                  >
                    <Upload
                      {...uploadProps}
                      listType="picture-card"
                      maxCount={1}
                      className="product-image-uploader"
                    >
                      {fileList.length === 0 && (
                        <div>
                          <PlusOutlined />
                          <div style={{ marginTop: 8 }}>Upload</div>
                        </div>
                      )}
                    </Upload>
                  </Form.Item>
                  
                  <div className="text-center text-sm text-gray-500">
                    <p>Recommended image size: 640x640 pixels</p>
                    <p>Maximum file size: 2MB</p>
                    <p>Supported formats: JPG, PNG, GIF</p>
                  </div>
                </div>
              </TabPane>
            </Tabs>
          </Form>
        ) : (
          <div className="flex justify-center items-center h-64">
            <Spin size="large" />
            <p className="ml-3 text-gray-500">Loading product data...</p>
          </div>
        )}
      </Spin>
    </Modal>
  );
};

export default EditModal;
